import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import {
  PublicClientApplication,
  AuthenticationResult,
  AccountInfo,
} from "@azure/msal-browser";
import { UserProfile } from "@/types/user";
import { extractUserProfile } from "@/utils/userHelpers";

// MSAL configuration for Office 365
const msalConfig = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID || "your-client-id-here",
    authority: import.meta.env.VITE_AZURE_TENANT_ID
      ? `https://login.microsoftonline.com/${
          import.meta.env.VITE_AZURE_TENANT_ID
        }`
      : "https://login.microsoftonline.com/common",
    redirectUri:
      import.meta.env.VITE_AZURE_REDIRECT_URI ||
      "http://localhost:8080/callback",
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
};

// Log configuration for debugging
// // console.log("MSAL Configuration:", {
//   clientId: msalConfig.auth.clientId,
//   authority: msalConfig.auth.authority,
//   redirectUri: msalConfig.auth.redirectUri,
//   hasTenantId: !!import.meta.env.VITE_AZURE_TENANT_ID,
//   hasClientId: !!import.meta.env.VITE_AZURE_CLIENT_ID,
// });

// Scopes for Office 365
const loginRequest = {
  scopes: ["User.Read", "email", "profile"],
};

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserProfile | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  checkAuthState: () => Promise<void>;
  setUser: (user: UserProfile | null) => void;
  setIsAuthenticated: (authenticated: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [msalInstance, setMsalInstance] =
    useState<PublicClientApplication | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeMsal = async () => {
      try {
        // console.log("Initializing MSAL...");
        const msal = new PublicClientApplication(msalConfig);
        await msal.initialize();
        setMsalInstance(msal);

        // Make MSAL instance available globally for callback page
        (window as any).msalInstance = msal;

        // console.log("MSAL initialized successfully");

        // Always try to handle redirect response first
        let redirectHandled = false;
        try {
          // console.log("Checking for redirect response...");
          const response = await msal.handleRedirectPromise();
          if (response && response.account) {
            // console.log("Redirect response received:", response);
            const userProfile = extractUserProfile(response.account);
            // console.log("User profile extracted:", userProfile);
            setUser(userProfile);
            setIsAuthenticated(true);
            redirectHandled = true;
            // console.log("User authenticated from redirect");
          } else {
            // console.log("No redirect response found");
          }
        } catch (error) {
          console.error("Error handling redirect:", error);
        }

        // Only check existing accounts if no redirect was handled
        if (!redirectHandled) {
          // console.log("Checking for existing accounts...");
          const accounts = msal.getAllAccounts();
          // console.log("Found accounts:", accounts);
          if (accounts.length > 0) {
            // console.log("Found existing account:", accounts[0]);
            const userProfile = extractUserProfile(accounts[0]);
            // console.log("User profile from existing account:", userProfile);
            setUser(userProfile);
            setIsAuthenticated(true);
          } else {
            // console.log("No existing accounts found");
          }
        }
      } catch (error) {
        console.error("MSAL initialization failed:", error);
      } finally {
        setLoading(false);
        // console.log("MSAL initialization complete, loading set to false");
      }
    };

    initializeMsal();
  }, []);

  const login = async () => {
    if (!msalInstance) return;

    try {
      setLoading(true);
      // Use redirect for callback flow
      await msalInstance.loginRedirect(loginRequest);
    } catch (error) {
      console.error("Login failed:", error);
      // Show user-friendly error message
      alert("فشل في تسجيل الدخول. يرجى المحاولة مرة أخرى.");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    if (!msalInstance) return;

    try {
      setLoading(true);
      // Use redirect for consistency with login flow
      await msalInstance.logoutRedirect();
    } catch (error) {
      console.error("Logout failed:", error);
      // If redirect fails, clear local state
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const checkAuthState = async () => {
    if (!msalInstance) {
      // console.log("No MSAL instance available");
      return;
    }

    try {
      setLoading(true);
      // console.log("Checking auth state...");

      // Redirect handling happens once, in initializeMsal above.
      // Only inspect the account cache here to avoid overlapping
      // handleRedirectPromise() calls, which MSAL rejects with
      // "interaction_in_progress".

      // Check for existing accounts
      const accounts = msalInstance.getAllAccounts();
      // console.log("All accounts found:", accounts);

      if (accounts.length > 0) {
        // console.log("Found existing account:", accounts[0]);
        const userProfile = extractUserProfile(accounts[0]);
        // console.log("User profile from existing account:", userProfile);
        setUser(userProfile);
        setIsAuthenticated(true);
        // console.log("Auth state updated from existing account");
      } else {
        // console.log("No existing accounts found");
        setUser(null);
        setIsAuthenticated(false);
        // console.log("Auth state cleared - no accounts");
      }
    } catch (error) {
      console.error("Error checking auth state:", error);
    } finally {
      setLoading(false);
      // console.log("Auth state check complete, loading set to false");
    }
  };

  // Add a polling mechanism to continuously check auth state
  useEffect(() => {
    if (!msalInstance || isAuthenticated) return;

    // console.log("Setting up auth state polling...");
    const interval = setInterval(() => {
      // console.log("Polling auth state...");
      checkAuthState();
    }, 2000); // Check every 2 seconds

    return () => {
      // console.log("Clearing auth state polling...");
      clearInterval(interval);
    };
  }, [msalInstance, isAuthenticated]);

  const value: AuthContextType = {
    isAuthenticated,
    user,
    login,
    logout,
    loading,
    checkAuthState,
    setUser,
    setIsAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
